import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Layers,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  ChevronRight,
  ChevronLeft,
  ListOrdered,
  Save,
  User,
  Stethoscope,
  CheckCircle2,
  Clock,
  XCircle,
  Play,
  Eye,
  ArrowLeft,
  MessageSquare,
  Mail,
  AlertCircle,
  Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status Colors
const EXECUTION_STATUS_COLORS = {
  pending: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  running: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  waiting_for_user: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
};

export const AgentQueuesPage = () => {
  const { t } = useTranslation('workflows');
  // List state
  const [queues, setQueues] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingQueue, setEditingQueue] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    task_sequence: [],
    entity_type: 'general',
    entity_id: '',
    status: 'active'
  });

  // Detail view state
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [queueExecutions, setQueueExecutions] = useState([]);
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [executionDetail, setExecutionDetail] = useState(null);
  const [executionDetailLoading, setExecutionDetailLoading] = useState(false);

  useEffect(() => {
    fetchQueues();
    fetchTasks();
  }, [page, statusFilter, entityTypeFilter]);

  const fetchQueues = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '12'
      });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (entityTypeFilter !== 'all') params.append('entity_type', entityTypeFilter);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API_URL}/api/workflows/agent-queues?${params}`);
      setQueues(response.data.data);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      toast.error(t('agentQueues.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workflows/tasks?limit=100&status=active`);
      setTasks(response.data.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchQueues();
  };

  const openForm = (queue = null) => {
    if (queue) {
      setEditingQueue(queue);
      setFormData({
        name: queue.name,
        description: queue.description || '',
        task_sequence: queue.task_sequence || [],
        entity_type: queue.entity_type || 'general',
        entity_id: queue.entity_id || '',
        status: queue.status
      });
    } else {
      setEditingQueue(null);
      setFormData({
        name: '',
        description: '',
        task_sequence: [],
        entity_type: 'general',
        entity_id: '',
        status: 'active'
      });
    }
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error(t('agentQueues.messages.nameRequired'));
      return;
    }
    
    setFormLoading(true);
    try {
      if (editingQueue) {
        await axios.put(`${API_URL}/api/workflows/agent-queues/${editingQueue.id}`, formData);
        toast.success(t('agentQueues.messages.updated'));
      } else {
        await axios.post(`${API_URL}/api/workflows/agent-queues`, formData);
        toast.success(t('agentQueues.messages.created'));
      }
      setShowForm(false);
      fetchQueues();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('agentQueues.messages.saveError'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (queueId) => {
    if (!window.confirm(t('agentQueues.confirmDelete'))) return;
    
    try {
      await axios.delete(`${API_URL}/api/workflows/agent-queues/${queueId}`);
      toast.success(t('agentQueues.messages.deleted'));
      fetchQueues();
    } catch (error) {
      toast.error(t('agentQueues.messages.deleteError'));
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(t('agentQueues.confirmDeleteAll', { count: queues.length }))) return;
    
    try {
      // Delete all queues one by one
      const deletePromises = queues.map(queue => 
        axios.delete(`${API_URL}/api/workflows/agent-queues/${queue.id}`)
      );
      await Promise.all(deletePromises);
      toast.success(t('agentQueues.messages.deletedAll', { count: queues.length }));
      fetchQueues();
    } catch (error) {
      toast.error(t('agentQueues.messages.deleteError'));
      fetchQueues(); // Refresh to see which ones were deleted
    }
  };

  const toggleTask = (taskId) => {
    if (formData.task_sequence.includes(taskId)) {
      setFormData({
        ...formData,
        task_sequence: formData.task_sequence.filter(id => id !== taskId)
      });
    } else {
      setFormData({
        ...formData,
        task_sequence: [...formData.task_sequence, taskId]
      });
    }
  };

  const viewQueueDetails = async (queue) => {
    setExecutionsLoading(true);
    setSelectedQueue(queue);
    setQueueExecutions([]);
    
    try {
      // Get queue details with executions
      const response = await axios.get(`${API_URL}/api/workflows/agent-queues/${queue.id}/executions`);
      setSelectedQueue(response.data.queue);
      setQueueExecutions(response.data.executions);
    } catch (error) {
      toast.error(t('agentQueues.messages.detailLoadError'));
      // Fallback to basic queue details
      try {
        const basicResponse = await axios.get(`${API_URL}/api/workflows/agent-queues/${queue.id}`);
        setSelectedQueue(basicResponse.data);
      } catch (e) {
        console.error('Error fetching queue:', e);
      }
    } finally {
      setExecutionsLoading(false);
    }
  };

  const viewExecutionDetail = async (execution) => {
    setSelectedExecution(execution);
    setExecutionDetailLoading(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/workflows/task-executions/${execution.id}`);
      setExecutionDetail(response.data);
    } catch (error) {
      toast.error(t('agentQueues.messages.executionLoadError'));
      // Fallback to basic execution data
      setExecutionDetail(execution);
    } finally {
      setExecutionDetailLoading(false);
    }
  };

  const closeQueueDetails = () => {
    setSelectedQueue(null);
    setQueueExecutions([]);
    setSelectedExecution(null);
    setExecutionDetail(null);
  };

  const closeExecutionDetail = () => {
    setSelectedExecution(null);
    setExecutionDetail(null);
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case 'patient': return <User className="h-4 w-4" />;
      case 'specialist': return <Stethoscope className="h-4 w-4" />;
      default: return <Layers className="h-4 w-4" />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'paused': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'completed': return <CheckCircle2 className="h-4 w-4 text-blue-500" />;
      default: return <XCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  // If showing execution detail
  if (selectedExecution && executionDetail) {
    return (
      <div className="space-y-6" data-testid="execution-detail-view">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={closeExecutionDetail}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('agentQueues.detail.backToQueue')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('agentQueues.execution.title')}</h1>
            <p className="text-gray-500 dark:text-gray-400">{executionDetail.task?.name || t('agentQueues.execution.task')}</p>
          </div>
        </div>

        {executionDetailLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Execution Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-orange-600" />
                  {t('agentQueues.execution.info')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <Badge className={EXECUTION_STATUS_COLORS[executionDetail.status]}>
                    {t(`agentQueues.status.${executionDetail.status}`) || executionDetail.status}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">{t('agentQueues.execution.started')}</p>
                    <p className="font-medium">
                      {executionDetail.started_at 
                        ? new Date(executionDetail.started_at).toLocaleString() 
                        : t('agentQueues.execution.notStarted')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">{t('agentQueues.execution.completed')}</p>
                    <p className="font-medium">
                      {executionDetail.completed_at 
                        ? new Date(executionDetail.completed_at).toLocaleString() 
                        : t('agentQueues.execution.inProgress')}
                    </p>
                  </div>
                </div>

                {executionDetail.task && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">{t('agentQueues.execution.task')}: {executionDetail.task.name}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{executionDetail.task.description}</p>
                    <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('agentQueues.execution.instruction')}:</p>
                      <p className="text-sm">{executionDetail.task.instruction}</p>
                    </div>
                  </div>
                )}

                {executionDetail.error && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-800">
                    <p className="text-sm font-medium text-red-800 dark:text-red-300 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      {t('agentQueues.execution.error')}
                    </p>
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">{executionDetail.error}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Output */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-blue-600" />
                  {t('agentQueues.execution.result')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {executionDetail.output ? (
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono">
                      {executionDetail.output}
                    </pre>
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">{t('agentQueues.execution.noResult')}</p>
                )}
              </CardContent>
            </Card>

            {/* Input Context */}
            {executionDetail.input_context && Object.keys(executionDetail.input_context).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('agentQueues.execution.inputContext')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm overflow-auto max-h-64">
                    {JSON.stringify(executionDetail.input_context, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            )}

            {/* Conversation Info */}
            {executionDetail.conversation && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {executionDetail.conversation.channel === 'email' ? (
                      <Mail className="h-5 w-5 text-purple-600" />
                    ) : (
                      <MessageSquare className="h-5 w-5 text-green-600" />
                    )}
                    {t('agentQueues.execution.relatedConversation')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">{t('agentQueues.execution.channel')}</p>
                      <Badge variant="outline">{executionDetail.conversation.channel}</Badge>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">{t('agentQueues.filters.status')}</p>
                      <Badge variant="outline">{executionDetail.conversation.status}</Badge>
                    </div>
                  </div>
                  
                  {executionDetail.conversation.subject && (
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">{t('agentQueues.execution.subject')}</p>
                      <p className="font-medium">{executionDetail.conversation.subject}</p>
                    </div>
                  )}

                  {executionDetail.conversation.messages?.length > 0 && (
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">{t('agentQueues.execution.messages', { count: executionDetail.conversation.messages.length })}</p>
                      <ScrollArea className="h-48 border rounded-lg p-2">
                        {executionDetail.conversation.messages.map((msg, idx) => (
                          <div 
                            key={idx} 
                            className={`p-2 rounded mb-2 ${
                              msg.sender_type === 'customer' ? 'bg-gray-100 dark:bg-gray-800' : 
                              msg.sender_type === 'system' ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-green-50 dark:bg-green-900/30'
                            }`}
                          >
                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                              <span className="font-medium">
                                {t(`agentQueues.execution.senderTypes.${msg.sender_type}`)}
                              </span>
                              <span>{new Date(msg.created_at).toLocaleTimeString()}</span>
                            </div>
                            <p className="text-sm">{msg.content}</p>
                          </div>
                        ))}
                      </ScrollArea>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    );
  }

  // If showing queue details
  if (selectedQueue) {
    return (
      <div className="space-y-6" data-testid="queue-detail-view">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={closeQueueDetails}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('agentQueues.detail.back')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{selectedQueue.name}</h1>
            <p className="text-gray-500 dark:text-gray-400">{selectedQueue.description || t('agentQueues.title')}</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Badge variant={selectedQueue.status === 'active' ? 'default' : 'secondary'}>
            {t(`agentQueues.status.${selectedQueue.status}`)}
          </Badge>
          <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
            {getEntityIcon(selectedQueue.entity_type)}
            {t(`agentQueues.entityTypes.${selectedQueue.entity_type}`)}
          </span>
        </div>

        <Separator />

        {/* Tasks in Queue */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ListOrdered className="h-5 w-5 text-orange-600" />
              {t('agentQueues.detail.queueTasks', { count: selectedQueue.tasks?.length || 0 })}
            </CardTitle>
            <CardDescription>{t('agentQueues.detail.queueTasksDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {selectedQueue.tasks?.length > 0 ? (
              <div className="space-y-2">
                {selectedQueue.tasks.map((task, index) => (
                  <div 
                    key={task.id} 
                    className="p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="h-8 w-8 rounded-full bg-[#E63946] text-white text-sm flex items-center justify-center font-medium">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium">{task.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">{task.instruction}</p>
                      </div>
                      <Badge variant="outline">
                        {task.closure_mode === 'system' ? `⚙️ ${t('agentQueues.detail.auto')}` : `👤 ${t('agentQueues.detail.manual')}`}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('agentQueues.detail.noTasksConfigured')}</p>
            )}
          </CardContent>
        </Card>

        {/* Task Executions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="h-5 w-5 text-green-600" />
              {t('agentQueues.detail.executionHistory', { count: queueExecutions.length })}
            </CardTitle>
            <CardDescription>{t('agentQueues.detail.executionHistoryDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {executionsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[#E63946]" />
              </div>
            ) : queueExecutions.length > 0 ? (
              <div className="space-y-2">
                {queueExecutions.map((execution) => (
                  <div 
                    key={execution.id} 
                    className="p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                    onClick={() => viewExecutionDetail(execution)}
                    data-testid={`execution-${execution.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge className={EXECUTION_STATUS_COLORS[execution.status]}>
                          {t(`agentQueues.status.${execution.status}`) || execution.status}
                        </Badge>
                        <div>
                          <p className="font-medium">{execution.task?.name || t('agentQueues.execution.task')}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(execution.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {execution.conversation && (
                          <Badge variant="outline" className="text-xs">
                            {execution.conversation.channel === 'email' ? (
                              <Mail className="h-3 w-3 mr-1" />
                            ) : (
                              <MessageSquare className="h-3 w-3 mr-1" />
                            )}
                            {execution.conversation.channel}
                          </Badge>
                        )}
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4 mr-1" />
                          {t('agentQueues.detail.viewDetails')}
                        </Button>
                      </div>
                    </div>
                    {execution.output && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 line-clamp-2 bg-gray-50 dark:bg-gray-800 p-2 rounded">
                        {execution.output.substring(0, 200)}...
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Play className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                <p>{t('agentQueues.detail.noExecutions')}</p>
                <p className="text-sm mt-1">{t('agentQueues.detail.executionsAppear')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main list view
  return (
    <div className="space-y-6" data-testid="agent-queues-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('agentQueues.title')}</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{t('agentQueues.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={t('agentQueues.searchPlaceholder')}
              className="pl-10"
              data-testid="queue-search"
            />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter">
            <SelectValue placeholder={t('agentQueues.filters.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('agentQueues.filters.all')}</SelectItem>
            <SelectItem value="active">{t('agentQueues.status.active')}</SelectItem>
            <SelectItem value="paused">{t('agentQueues.status.paused')}</SelectItem>
            <SelectItem value="completed">{t('agentQueues.status.completed')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={entityTypeFilter} onValueChange={(v) => { setEntityTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="entity-filter">
            <SelectValue placeholder={t('agentQueues.filters.entity')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('agentQueues.entityTypes.all')}</SelectItem>
            <SelectItem value="general">{t('agentQueues.entityTypes.general')}</SelectItem>
            <SelectItem value="patient">{t('agentQueues.entityTypes.patient')}</SelectItem>
            <SelectItem value="specialist">{t('agentQueues.entityTypes.specialist')}</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => openForm()} className="bg-[#E63946] hover:bg-[#d32836]" data-testid="new-queue-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t('agentQueues.newQueue')}
        </Button>
        {queues.length > 0 && (
          <Button 
            variant="outline" 
            onClick={handleDeleteAll}
            className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:hover:bg-red-900/30"
            data-testid="delete-all-queues-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t('agentQueues.deleteAll')}
          </Button>
        )}
      </div>

      {/* Queues Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
        </div>
      ) : queues.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {queues.map(queue => (
            <Card 
              key={queue.id} 
              className="group hover:shadow-lg transition-all duration-200 hover:-translate-y-1 cursor-pointer border-[var(--line)] bg-[var(--surface)]" 
              onClick={() => viewQueueDetails(queue)}
              data-testid={`queue-card-${queue.id}`}
              style={{ borderRadius: 'var(--r-lg, 12px)' }}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-xl bg-[var(--red-soft,#FBE6E8)] flex items-center justify-center">
                      <Layers className="h-7 w-7 text-[var(--red,#E80114)]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[17px] text-[var(--ink,#1A1414)] leading-tight">{queue.name}</p>
                      <div className="flex items-center gap-2 mt-2">
                        {getStatusIcon(queue.status)}
                        <Badge variant={queue.status === 'active' ? 'default' : 'secondary'} className="text-[12px] px-3 py-1 font-semibold">
                          {t(`agentQueues.status.${queue.status}`)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
                
                {queue.description && (
                  <p className="text-[14px] text-[var(--ink-2,#4A3F3F)] mb-4 line-clamp-2 leading-relaxed">{queue.description}</p>
                )}
                
                <div className="flex items-center gap-5 text-[14px] text-[var(--ink-2,#4A3F3F)] mb-4">
                  <span className="flex items-center gap-2">
                    <ListOrdered className="h-5 w-5 text-[var(--ink-3)]" />
                    {queue.task_count || 0} {t('agentQueues.tasks')}
                  </span>
                  <span className="flex items-center gap-2">
                    {getEntityIcon(queue.entity_type)}
                    {t(`agentQueues.entityTypes.${queue.entity_type}`)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-[var(--line-soft,#F4F0EC)]" onClick={(e) => e.stopPropagation()}>
                  <span className="text-[13px] text-[var(--ink-3,#8B7E7E)] font-medium">
                    {new Date(queue.created_at).toLocaleDateString()}
                  </span>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" onClick={() => openForm(queue)} data-testid={`edit-queue-${queue.id}`} className="h-9 w-9 p-0">
                      <Edit className="h-5 w-5 text-[var(--ink-3)]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(queue.id)} data-testid={`delete-queue-${queue.id}`} className="h-9 w-9 p-0">
                      <Trash2 className="h-5 w-5 text-[var(--red,#E80114)]" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Layers className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <p>{t('agentQueues.noQueues')}</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="flex items-center px-4 text-sm">
            {t('agentQueues.page', { current: page, total: totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Queue Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQueue ? t('agentQueues.form.editTitle') : t('agentQueues.form.title')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('agentQueues.form.name')} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder={t('agentQueues.form.namePlaceholder')}
                data-testid="queue-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('agentQueues.form.description')}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder={t('agentQueues.form.descriptionPlaceholder')}
                rows={2}
                data-testid="queue-description"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('agentQueues.form.entityType')}</Label>
                <Select
                  value={formData.entity_type}
                  onValueChange={(v) => setFormData({...formData, entity_type: v})}
                >
                  <SelectTrigger data-testid="queue-entity-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">{t('agentQueues.entityTypes.general')}</SelectItem>
                    <SelectItem value="patient">{t('agentQueues.entityTypes.patient')}</SelectItem>
                    <SelectItem value="specialist">{t('agentQueues.entityTypes.specialist')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('agentQueues.form.status')}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({...formData, status: v})}
                >
                  <SelectTrigger data-testid="queue-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('agentQueues.status.active')}</SelectItem>
                    <SelectItem value="paused">{t('agentQueues.status.paused')}</SelectItem>
                    <SelectItem value="completed">{t('agentQueues.status.completed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Task Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <ListOrdered className="h-4 w-4" />
                {t('agentQueues.form.taskSequence')}
              </Label>
              <ScrollArea className="h-48 border rounded-lg p-2">
                {tasks.length > 0 ? (
                  <div className="space-y-2">
                    {tasks.map(task => (
                      <div
                        key={task.id}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                          formData.task_sequence.includes(task.id) ? 'bg-[#FDE8EA] border border-[#E63946] dark:bg-red-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                        onClick={() => toggleTask(task.id)}
                      >
                        <input
                          type="checkbox"
                          checked={formData.task_sequence.includes(task.id)}
                          onChange={() => toggleTask(task.id)}
                          className="rounded"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{task.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {task.closure_mode === 'system' ? `⚙️ ${t('agentQueues.detail.auto')}` : `👤 ${t('agentQueues.detail.manual')}`}
                          </p>
                        </div>
                        {formData.task_sequence.includes(task.id) && (
                          <Badge className="bg-[#E63946]">
                            #{formData.task_sequence.indexOf(task.id) + 1}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-4">{t('agentQueues.form.noTasks')}</p>
                )}
              </ScrollArea>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('agentQueues.form.taskOrder')}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={formLoading}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={formLoading} data-testid="save-queue-btn">
              {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editingQueue ? t('common.update') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentQueuesPage;
